/**
 * OmegaCeoDecisionsPage — Phase 36.7a.
 *
 * Full strategic decision queue with priority + brain filters.
 * Polls every 30 s. Items sorted by rank_score DESC (API-provided).
 */

import { useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface DecisionItem {
  item_id: string;
  source_brain: string;
  item_type: string;
  priority: 'P0' | 'P1' | 'P2' | 'P3';
  age_seconds: number;
  summary: string;
  action_url: string;
  rank_score: number;
}

interface DecisionQueueResponse {
  items: DecisionItem[];
  total_considered: number;
  limit_applied: number;
}

// ── Helpers (exported for tests) ─────────────────────────────────────────────

export const PRIORITY_COLORS: Record<string, string> = {
  P0: '#ef4444',
  P1: '#f97316',
  P2: '#fbbf24',
  P3: '#6b7280',
};

export function fmtAge(ageSeconds: number): string {
  if (ageSeconds < 3600)  return `${Math.floor(ageSeconds / 60)}m ago`;
  if (ageSeconds < 86400) return `${Math.floor(ageSeconds / 3600)}h ago`;
  return `${Math.floor(ageSeconds / 86400)}d ago`;
}

export function brainLabel(brain: string): string {
  const LABELS: Record<string, string> = {
    marketing:   'Marketing',
    sales:       'Sales',
    customer:    'Customers',
    finance:     'Finance',
    alpha_omega: 'ALPHA-OMEGA',
  };
  return LABELS[brain] ?? brain;
}

export function urgencyFlag(ageSeconds: number): string {
  if (ageSeconds > 7 * 86400) return '🔴 Very urgent';
  if (ageSeconds > 48 * 3600) return '⚠ Urgent';
  return '';
}

export function priorityLabel(p: string): string {
  const MAP: Record<string, string> = {
    P0: 'Critical',
    P1: 'High',
    P2: 'Medium',
    P3: 'Low',
  };
  return MAP[p] ?? p;
}

// ── D2: drag-and-drop priority ordering (pure helpers, exported for tests) ─────

/** Move the element at `from` to `to`, returning a new array (no mutation). */
export function reorder<T>(list: readonly T[], from: number, to: number): T[] {
  const next = list.slice();
  if (from < 0 || from >= next.length || to < 0 || to >= next.length || from === to) {
    return next;
  }
  // Bounds already guarded above, so the removed element is defined.
  const moved = next.splice(from, 1)[0] as T;
  next.splice(to, 0, moved);
  return next;
}

/**
 * Apply a founder-pinned `order` (list of item_ids) over `items`: ids present in
 * `order` come first in that sequence; everything else keeps its original
 * relative order. Mirrors the backend ranker (overrides pinned ahead, rest by
 * rank). `order = null` is a no-op (use the API order as-is).
 */
export function applyManualOrder(
  items: readonly DecisionItem[],
  order: readonly string[] | null,
): DecisionItem[] {
  if (!order || order.length === 0) return items.slice();
  const pos = new Map(order.map((id, i) => [id, i]));
  const pinned = items.filter(i => pos.has(i.item_id));
  pinned.sort((a, b) => (pos.get(a.item_id) ?? 0) - (pos.get(b.item_id) ?? 0));
  const rest = items.filter(i => !pos.has(i.item_id));
  return [...pinned, ...rest];
}

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchDecisionQueue(): Promise<DecisionQueueResponse> {
  return apiFetch<DecisionQueueResponse>('/internal/ceo/decision-queue?limit=50');
}

/**
 * D2 — persist the founder's drag-and-drop ordering. Replace-all semantics: the
 * full ordered id list is sent; an empty list clears all overrides. AdminOnly on
 * the server (POST /internal/ceo/decision-queue/priority). apiFetch signs the body
 * under the operator key and canonicalises it (RFC 8785).
 */
async function saveDecisionPriority(orderedItemIds: string[]): Promise<void> {
  await apiFetch<void>('/internal/ceo/decision-queue/priority', {
    method: 'POST',
    body: { ordered_item_ids: orderedItemIds },
  });
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
};

// ── Page ─────────────────────────────────────────────────────────────────────

export function OmegaCeoDecisionsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const queryClient = useQueryClient();
  const [priorityFilter, setPriorityFilter] = useState('');
  const [brainFilter, setBrainFilter]       = useState('');
  const [manualOrder, setManualOrder] = useState<string[] | null>(null);
  const [dragIndex, setDragIndex]     = useState<number | null>(null);
  const [saveError, setSaveError]     = useState<string | null>(null);

  const { data, isPending, isError } = useQuery({
    queryKey: ['ceo', 'decision-queue'],
    queryFn: fetchDecisionQueue,
    refetchInterval: 30_000,
  });

  // Drag-reorder is only coherent over the full, unfiltered list.
  const dragEnabled = priorityFilter === '' && brainFilter === '';

  const items = useMemo(() => {
    let list = data?.items ?? [];
    if (priorityFilter) list = list.filter(i => i.priority === priorityFilter);
    if (brainFilter)    list = list.filter(i => i.source_brain === brainFilter);
    return applyManualOrder(list, dragEnabled ? manualOrder : null);
  }, [data, priorityFilter, brainFilter, manualOrder, dragEnabled]);

  const persistOrder = async (ids: string[]) => {
    setSaveError(null);
    try {
      await saveDecisionPriority(ids);
      await queryClient.invalidateQueries({ queryKey: ['ceo', 'decision-queue'] });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save order');
    }
  };

  const handleDrop = (dropIndex: number) => {
    const from = dragIndex;
    setDragIndex(null);
    if (from === null || from === dropIndex) return;
    const next = reorder(items, from, dropIndex);
    const ids = next.map(i => i.item_id);
    setManualOrder(ids);
    void persistOrder(ids);
  };

  const resetOrder = () => {
    setManualOrder(null);
    void persistOrder([]);
  };

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'CEO', to: '/omega/ceo' },
        { label: 'Decision Queue' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 8, direction: 'ltr' }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Strategic Decision Queue
        </h1>
        {data && (
          <span style={{
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)',
          }}>
            {items.length}
          </span>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center', direction: 'ltr' }}>
        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} style={inputStyle}>
          <option value="">All priorities</option>
          <option value="P0">P0 — Critical</option>
          <option value="P1">P1 — High</option>
          <option value="P2">P2 — Medium</option>
          <option value="P3">P3 — Low</option>
        </select>
        <select value={brainFilter} onChange={e => setBrainFilter(e.target.value)} style={inputStyle}>
          <option value="">All brains</option>
          <option value="marketing">Marketing</option>
          <option value="sales">Sales</option>
          <option value="customer">Customers</option>
          <option value="finance">Finance</option>
          <option value="alpha_omega">ALPHA-OMEGA</option>
        </select>
      </div>

      {/* D2 — drag-to-reorder priority */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, direction: 'ltr', fontSize: '8pt', color: 'var(--omega-ink-subtle)' }}>
        {dragEnabled ? (
          <>
            <span>Drag rows to set founder priority order.</span>
            {manualOrder && (
              <button type="button" onClick={resetOrder} style={{ ...inputStyle, cursor: 'pointer' }}>
                Reset order
              </button>
            )}
          </>
        ) : (
          <span style={{ fontStyle: 'italic' }}>Clear filters to reorder priority.</span>
        )}
        {saveError && <span style={{ color: '#f87171' }}>Order save failed: {saveError}</span>}
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load the decision queue.</div>}

      {!isPending && !isError && items.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center', direction: 'ltr' }}>
          No pending items — all clear ✓
        </div>
      )}

      {items.map((item, index) => {
        const flag = urgencyFlag(item.age_seconds);
        const isUrgent = item.priority === 'P0' || item.priority === 'P1' || flag !== '';
        return (
          <div
            key={item.item_id}
            draggable={dragEnabled}
            onDragStart={() => setDragIndex(index)}
            onDragEnd={() => setDragIndex(null)}
            onDragOver={dragEnabled ? (e) => e.preventDefault() : undefined}
            onDrop={dragEnabled ? () => handleDrop(index) : undefined}
            style={{
              display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
              padding: '10px 12px',
              borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
              borderLeft: isUrgent ? '3px solid #ef4444' : '3px solid transparent',
              direction: 'ltr',
              cursor: dragEnabled ? 'grab' : 'default',
              opacity: dragIndex === index ? 0.4 : 1,
            }}
          >
            {dragEnabled && (
              <span aria-hidden style={{ color: 'var(--omega-ink-subtle)', fontSize: '10pt', cursor: 'grab', userSelect: 'none' }}>⠿</span>
            )}
            {/* Priority badge */}
            <span style={{
              fontSize: '7.5pt', fontWeight: 700, padding: '2px 7px',
              border: `1px solid ${PRIORITY_COLORS[item.priority] ?? '#6b7280'}`,
              color: PRIORITY_COLORS[item.priority] ?? '#6b7280',
              borderRadius: 3, letterSpacing: '0.06em', textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
              title={priorityLabel(item.priority)}
            >
              {item.priority}
            </span>

            {/* Brain label */}
            <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', whiteSpace: 'nowrap' }}>
              {brainLabel(item.source_brain)}
            </span>

            {/* Summary */}
            <span style={{ fontSize: '9.5pt', flex: 1, minWidth: 180, direction: 'ltr' }}>
              {flag && (
                <span style={{ color: flag.startsWith('🔴') ? '#ef4444' : '#fbbf24', marginLeft: 6, fontSize: '8pt' }}>
                  {flag}
                </span>
              )}
              {item.summary}
            </span>

            {/* Age */}
            <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', whiteSpace: 'nowrap' }}>
              {fmtAge(item.age_seconds)}
            </span>

            {/* Action link */}
            <a
              href={item.action_url}
              style={{
                background: 'rgba(251,191,36,0.12)',
                border: '1px solid rgba(251,191,36,0.35)',
                borderRadius: 4, color: '#fbbf24',
                fontFamily: 'var(--omega-mono)', fontSize: '8pt', fontWeight: 600,
                padding: '4px 10px', textDecoration: 'none', whiteSpace: 'nowrap',
                maxWidth: 480,
              }}
            >
              Handle →
            </a>
          </div>
        );
      })}
    </div>
  );
}
